/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'
import jwtDecode from 'jwt-decode'

/**************************************************************************************************/
/** @import Components/Global                                                                    **/
/**************************************************************************************************/
import * as LoginFooter from '@/Components/Global/LoginFooter/template.vue'
import * as LoginForm from '@/Components/Global/LoginForm/template.vue'
import * as LoginHeader from '@/Components/Global/LoginHeader/template.vue'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { VueRouter } from '@/Constants/VueRouter'

/**************************************************************************************************/
/** @import Enums                                                                                **/
/**************************************************************************************************/
import { LoginTypes } from '@/Enums/LoginTypes'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { NotificationsHelper } from '@/Helpers/Utils/NotificationsHelper'
import { RouterHelper } from '@/Helpers/Utils/RouterHelper'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinBase from '@/Mixins/MixinBase'
import MixinResponsive from '@/Mixins/MixinResponsive'

/**************************************************************************************************/
/** @import Store/Global                                                                         **/
/**************************************************************************************************/
import Store from '@/Store/Global/Default'

/**************************************************************************************************/
/** @internal Const @name LoginView                                                              **/
/**************************************************************************************************/
const LoginView = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: VueRouter.Views.Global.LOGIN.NAME, mixins: [MixinBase, MixinResponsive],

	components:
	{
		LoginFooter: LoginFooter.default,
		LoginHeader: LoginHeader.default,
		LoginForm: LoginForm.default
	},

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			maxLoadTime: 20000,
			type: LoginTypes.User
		}
	},

	/*************************************************/
	/** @Hooks                                      **/
	/*************************************************/
	created() : void
	{
		if (NotificationsHelper.isNotificationSupported())
		{
			// La vista de Login corresponde a la primera que es cargada cuando
			// el usuario entra al sitio web, realizar aquí la validación de los
			// permisos para poder Notificar al usuario posteriormente.
			const { permission } = Notification

			// Si no se tiene explicitamente los permisos, verificar si se le solicitaron
			// previamente al Usuario.
			if (permission === 'default') NotificationsHelper.askNotificationPermissions()

			// Por lo contrario, si ya fueron negados por el Usuario, simplemente informarle
			// que se encuentran negados en caso que el decida otorgarlos en un futuro.
			else if (permission === 'denied') this.ShowToast('Notificaciones Deshabilitadas', 'Las Notificaciones no estarán disponibles hasta que usted autorice poder recibirlas.', 'danger')
		}
	},

	mounted() : void
	{
		const viewParams = this.$route.params

		// Desde ambiente Planta, al crear un CheckList, el usuario es redireccionado a esta vista, mantener el tipo de Login.
		if (viewParams.LoginType === LoginTypes.Storage.toString())
		{
			this.type = LoginTypes.Storage
			this.LoginForm.UpdateType(this.type)
		}

		// El parametro 'successLogout' se encuentra definido unicamente cuando se redirecciona a esta vista al cerrar sesión.
		if (viewParams.successLogout === 'true')
		{
			const message = this.IsTypeStorage && viewParams.WasCheckListCreated === 'true' ? 'El CheckList fue creado correctamente!' : 'Se a cerrado la sesión correctamente!'
			this.ShowToast(`Sesión Finalizada`, message, 'success')
		}
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		IsTypeStorage() : boolean
		{
			return this.type === LoginTypes.Storage
		},

		IsTypeUser() : boolean
		{
			return this.type === LoginTypes.User
		},

		LoginForm() : LoginForm.LoginFormRef
		{
			return this.$refs.loginForm as LoginForm.LoginFormRef
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		GetAccessText() : string
		{
			return this.IsTypeUser ? 'Cambiar a Acceso Planta' : 'Cambiar a Acceso Usuario'
		},

		OnAccessStorageClick() : void
		{
			this.type = this.IsTypeUser ? LoginTypes.Storage : LoginTypes.User
			this.LoginForm.UpdateType(this.type)
		},

		OnFailedLogin() : void
		{
			const title   = this.IsTypeUser ? 'Error al Iniciar Sesión' : 'Error al Autenticar Operador'
			const message = this.IsTypeUser ? 'Verifica que el email ingresado y/o la contraseña sean correctos' : 'Verifica que código planta y/o código operador sean correctos, o que la planta se encuentre con acceso'
			this.ShowToast(title, message, 'danger')
		},

		async OnSuccessLogin(response: any, password: string) : Promise<void>
		{
			const { accessToken } = response
			const user = jwtDecode(accessToken).data[0]
			
			// Emitir el evento de Inicio de Sesión al Servidor.
			Store.getters.getStoredSocket.emit('userLoggedIn', { channel: 'web', user })

			// Fin del Proceso de Inicio de Sesión.
			Store.commit('storeCredentials', { accessToken, user })

			// Antes de redireccionar, obtener los Permisos del Usuario.
			if (this.IsTypeUser)
			{
				const permissions = await Store.dispatch('fetchPermissionsRole', user._idRole)
				Store.commit('storeUserPermissions', permissions)
			}

			// Redireccionar a la vista correspondiente.
			if (this.IsTypeStorage) this.$router.push({ name: VueRouter.Views.Modules.CheckList.CreateView.NAME, params: { moduleEnvironment: 'Storage', successLogin: 'true' } })
			else RouterHelper.redirectToCorrectCPanelView(this as any, user)
		}
	}
})

/**************************************************************************************************/
/** @export Component @name LoginView                                                            **/
/**************************************************************************************************/
export default LoginView